import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import {
  ArrowDownFromLine,
  ArrowUpToLine,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

export interface StatsSummaryCard {
  key: string;
  label: string;
  value: React.ReactNode;
  icon: LucideIcon;
  valueClassName?: string;
  /** Optional pill rendered in the card's top-right corner (e.g. "Most Ordered"). */
  badge?: React.ReactNode;
}

interface StatsSummaryBlockProps {
  cards: StatsSummaryCard[];
  className?: string;
  defaultOpen?: boolean;
}

const GRID_COLS: Record<number, string> = {
  1: "grid-cols-1",
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4",
  5: "grid-cols-5",
};

/**
 * Collapsible analytics summary block used on the Reservations and Requests
 * screens. Purely presentational — callers supply the cards (label, value,
 * icon) and own the data fetching.
 */
export const StatsSummaryBlock: React.FC<StatsSummaryBlockProps> = ({
  cards,
  className,
  defaultOpen = true,
}) => {
  const { t } = useTranslation();
  const [showStats, setShowStats] = useState(defaultOpen);

  const now = new Date();
  const ToggleIcon = showStats ? ArrowDownFromLine : ArrowUpToLine;
  const gridColsClass = GRID_COLS[cards.length] ?? "grid-cols-4";

  return (
    <div className={cn("g-2 rounded-[8px] bg-neutral-50 p-4", className)}>
      <div className="flex items-center justify-between">
        <div className="text-[16px] text-[#CAC19F]">
          {format(now, "EEEE, ")}{" "}
          <span className="font-[500] text-[#CAC19F]">
            {format(now, "d MMMM yyyy")}{" "}
          </span>
          <span className="font-[500] text-[#443F2C]">
            {format(now, "HH:mm")}
          </span>
        </div>
        <Button
          type="button"
          variant="new_ghost"
          size="icon_sm"
          onClick={() => setShowStats((prev) => !prev)}
          aria-expanded={showStats}
          aria-label={
            showStats
              ? t("common.stats.hideAnalytics", "Hide analytics")
              : t("common.stats.showAnalytics", "Show analytics")
          }
        >
          <ToggleIcon className="text-monochrome-core-1 size-4" />
        </Button>
      </div>

      {showStats && (
        <div className={cn("mt-4 grid gap-4", gridColsClass)}>
          {cards.map(
            ({ key, label, value, icon: Icon, valueClassName, badge }) => (
              <Card
                key={key}
                className="relative flex flex-row items-center justify-between gap-2 rounded-[16px] px-[16px] py-[24px] shadow-none"
              >
                {badge ? (
                  <div className="absolute top-0 right-1">{badge}</div>
                ) : null}
                <div className="flex flex-row items-center gap-2">
                  <Icon className="size-6 text-[#CAC19F]" />
                  <div className="text-[16px] text-[#171717]">{label}</div>
                </div>

                <div className={cn("text-[16px] font-[700]", valueClassName)}>
                  {value}
                </div>
              </Card>
            ),
          )}
        </div>
      )}
    </div>
  );
};

export default StatsSummaryBlock;
